import {
  FILE_MAX_BYTES,
  FILE_TYPES,
  mediaTypeFromName,
  normalizeMediaType,
  type FileMediaType,
} from "@typedoc/contracts/files";

/** What the file picker is told it may offer. */
export const ACCEPT = Object.keys(FILE_TYPES).join(",");

/** What a browser calls a file whose type it does not know. */
const UNKNOWN_TYPES = new Set(["", "application/octet-stream", "application/vnd.ms-excel"]);

/**
 * The media type a chat would store a picked file as, or null when it takes none. The browser's
 * own type wins; a type it could not tell (a Markdown file is often blank, a CSV can arrive as an
 * Excel type on Windows) is read from the name.
 */
export function mediaTypeOf(file: { name: string; type: string }): FileMediaType | null {
  const declared = normalizeMediaType(file.type);
  if (declared) return declared;
  if (!UNKNOWN_TYPES.has(file.type.split(";")[0]?.trim().toLowerCase() ?? "")) return null;
  return mediaTypeFromName(file.name);
}

export type Refusal = "type" | "size" | "empty";

/** Why a picked file cannot be attached, before anything is sent, or null when it can. */
export function refusalOf(file: { name: string; type: string; size: number }): Refusal | null {
  if (mediaTypeOf(file) === null) return "type";
  if (file.size === 0) return "empty";
  if (file.size > FILE_MAX_BYTES) return "size";
  return null;
}

/** The sentence a refusal is shown as, naming the file. */
export function refusalText(name: string, refusal: Refusal): string {
  switch (refusal) {
    case "type":
      return `${name} can't be attached. Use a PDF, an image, a text file, or an Excel, Word or PowerPoint file.`;
    case "size":
      return `${name} is too large. The limit is ${String(FILE_MAX_BYTES / (1024 * 1024))} MB.`;
    case "empty":
      return `${name} is empty.`;
  }
}
